import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageLink, PageTitle } from '../../../_theme/layout/core'
import { ManageProjectStageToolbar } from '../../../_theme/layout/components/toolbar/ManageProjectStageToolbar'
import { Content } from '../../../_theme/layout/components/Content'
import { getProjectStageInfo, ISortProject, IProjectStage, downloadBusinessCaseReport } from '../../modules/project'
import { useLocation, useParams } from 'react-router-dom'
import { useAuth, usePermission } from '../../modules/auth'
import Loader from '../../modules/apps/components/Loader'
import NotFound from '../../modules/apps/components/NotFound'
import { AccessDenied } from '../../modules/apps/components/UI/RequirePermission'
import PermissionGate, { VIEW_ONLY_MESSAGE } from '../../modules/apps/components/UI/PermissionGate'
import { useSnackbar } from '../../../_theme/context/SnackbarContext'
import { buildNavigationState, envConfig, getDashboardLink, getDashboardStagegates, getProjectStageLink } from '../../../utils/config'
import ProjectInitiation from '../../modules/project/Stage/ProjectInitiation'
import InfrastructurePlanning from '../../modules/project/Stage/InfrastructurePlanning'
import StrategicResourcing from '../../modules/project/Stage/StrategicResourcing'
import Prefeasibility from '../../modules/project/Stage/Prefeasibility'
import { LeftStepperProvider } from '../../../_theme/context/LeftStepperContext'
import { ProjectStageProvider } from '../../modules/project/core/ProjectStageContext'
import { Home, ShieldCheck, Target, TriangleAlert, Trophy } from 'lucide-react'
import GateStepper from '../../modules/project/components/GateStepper'
import { getStageGate } from '../../../utils/stageGates'
import ScoreCard from '../../modules/apps/components/UI/ScoreCard'
import ThemeTooltip from '../../modules/apps/components/UI/ThemeTooltip'
import ComingSoon from '../../modules/apps/components/ComingSoon'
import ConceptAndViability from '../../modules/project/Stage/ConceptAndViability'
import { useAiAssistant } from '../../../_theme/context/AiAssistantContext'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Eye, FileDown } from 'lucide-react'
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

/** Stage statuses that can no longer be submitted (same list the stage screens lock on). */
const SUBMIT_LOCKED_STATUSES = ["stage_status_3", "stage_status_4", "stage_status_6"];

const ManageProjectStage = () => {
    const { auth } = useAuth();
    const { can, canStage } = usePermission();
    const { showSnackbar } = useSnackbar();
    const location = useLocation();
    const { project_id, dk_stage } = useParams();
    const canViewStage = canStage(dk_stage, 'view');
    const [pageLoader, setPageLoader] = useState<boolean>(true);
    const [downloadBusinessCaseReportLoader, setDownloadBusinessCaseReportLoader] = useState<boolean>(false);
    const [isApprover, seIsApprover] = useState<boolean>(false);
    const [readonlyAccess, setReadonlyAccess] = useState<boolean>(false);
    const [project, setProject] = useState<ISortProject | undefined>()
    const [stage, setStage] = useState<IProjectStage | undefined>()
    const [stages, setStages] = useState<IProjectStage[]>([]);
    const { setStage: setStageAiAssistant } = useAiAssistant();
    const abortRef = useRef<AbortController | null>(null);

    const fetchProjectStageInfo = useCallback(async (project_id: string, dk_stage: string) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setPageLoader(true);
            const projectStageResponse = await getProjectStageInfo(project_id, dk_stage, controller.signal);
            if (controller.signal.aborted) return;
            if (projectStageResponse.status == "ok") {
                setProject(projectStageResponse.data.project)
                setStage(projectStageResponse.data.stage);
                setStages(projectStageResponse.data.stages);
            } else {
                showSnackbar(projectStageResponse.message, 'danger');
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error('Failed to fetch projects:', error);
            showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), 'danger');
        } finally {
            if (!controller.signal.aborted) setPageLoader(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        if (project_id && dk_stage && canViewStage) {
            fetchProjectStageInfo(project_id, dk_stage);
            if (auth?.user_details?.is_approver && auth?.user_details?.is_approver === 'Yes') {
                seIsApprover(true);
            } else {
                seIsApprover(false);
            }
        }
        return () => abortRef.current?.abort();
    }, [project_id, dk_stage, fetchProjectStageInfo, auth?.user_details?.is_approver, canViewStage])

    useEffect(() => {
        if (stage && stage.active_gate == 0) {
            setReadonlyAccess(true);
        }
    }, [stage]);

    useEffect(() => {
        setStageAiAssistant(parseInt(dk_stage?.replace("project_stage_", "") || "0"));
    }, [dk_stage]);

    useEffect(() => {
        document.body.classList.add('bg-custom-gray');

        return () => {
            document.body.classList.remove('bg-custom-gray');
        };
    }, []);

    const stageName = useMemo(() => {
        return getStageGate(stage?.dk_stage || "")?.stageGate + " - " + stage?.dn_stage || "";
    }, [stage?.dk_stage, stage?.dn_stage]);

    const stageData = useMemo(() => {
        return getStageGate(stage?.dk_stage || "");
    }, [stage?.dk_stage]);


    if (!canViewStage) {
        return <AccessDenied />;
    }
    if (pageLoader) return <Loader loadingText='Loading projects…' />;
    if (!project || !stage || !auth?.user_id) {
        return <NotFound />;
    }
    // Without write permission the stage opens read-only (same mode approvers use).
    const isViewOnly = !canStage(dk_stage, 'write');
    const stageReadonly = readonlyAccess || isViewOnly;
    // Submit is its own right, independent of write; it needs a stage that can still be
    // submitted (active, not with the approver, not already submitted/approved/rejected).
    const canSubmit = canStage(dk_stage, 'submit')
        && !isApprover
        && !readonlyAccess
        && !SUBMIT_LOCKED_STATUSES.includes(stage.dk_stage_status);
    const handleLoadStage = () => {
        switch (dk_stage) {
            case "project_stage_0":
                return <ProjectInitiation stage={stage} isApprover={isApprover} readonlyAccess={stageReadonly} canSubmit={canSubmit} user_id={auth.user_id} project={project} />
            case "project_stage_1":
                return <InfrastructurePlanning stage={stage} isApprover={isApprover} readonlyAccess={stageReadonly} canSubmit={canSubmit} user_id={auth.user_id} project={project} />
            case "project_stage_2":
                return <StrategicResourcing stage={stage} isApprover={isApprover} readonlyAccess={stageReadonly} canSubmit={canSubmit} user_id={auth.user_id} project={project} />
            case "project_stage_3":
                return <Prefeasibility stage={stage} isApprover={isApprover} readonlyAccess={stageReadonly} canSubmit={canSubmit} user_id={auth.user_id} project={project} />
            case "project_stage_4":
                return <ConceptAndViability stage={stage} isApprover={isApprover} readonlyAccess={stageReadonly} canSubmit={canSubmit} user_id={auth.user_id} project={project} />
            default:
                return <ComingSoon linkText="Project Stages" linkUrl={`${getProjectStageLink(project.project_id)}/${location?.search}`} />
        }
    }
    const breadCrumbs: Array<PageLink> = isApprover ? [
        {
            title: 'My Pending Requests',
            path: getDashboardStagegates(),
            isSeparator: false,
            isActive: false,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: stage.dn_stage,
            path: '',
            isSeparator: false,
            isActive: true,
        },
    ] : [
        {
            title: 'Home',
            path: getDashboardLink(),
            isSeparator: false,
            isActive: false,
            icon: <Home size={18} />,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: 'My Projects',
            path: getDashboardStagegates(),
            isSeparator: false,
            isActive: false,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: "Project Stages",
            path: `${getProjectStageLink(project.project_id)}/${location?.search}`,
            isSeparator: false,
            isActive: false,
            state: buildNavigationState(location.state?.from)
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: `${getStageGate(stage.dk_stage)?.stageGate} - ${stage.dn_stage}`,
            path: '',
            isSeparator: false,
            isActive: true,
        },
    ];
    const getStageRightSection = () => {
        switch (dk_stage) {
            case "project_stage_2":
                return <div className="card card-xxl-stretch border-primary">
                    <div className="card-body d-flex flex-column justify-content-between px-3 py-1">
                        <div className="d-flex flex-column">
                            <div className='text-primary fw-bold fs-1 mb-0'>34%</div>
                            <div className='text-gray-600 fw-semibold fs-6'>Feasibility Score</div>
                        </div>
                    </div>
                </div>
            case "project_stage_3":
                return <div className="card card-xxl-stretch border-primary">
                    <div className="card-body d-flex flex-column justify-content-between px-3 py-1">
                        <div className="d-flex flex-column">
                            <div className='text-primary fw-bold fs-1 mb-0'>32%</div>
                            <div className='text-gray-600 fw-semibold fs-6'>Overall Readiness</div>
                        </div>
                    </div>
                </div>
            case "project_stage_4":
                return <div className="card card-xxl-stretch border-primary">
                    <div className="card-body d-flex flex-column justify-content-between px-3 py-1">
                        <div className="d-flex flex-column">
                            <div className='text-primary fw-bold fs-1 mb-0'>30%</div>
                            <div className='text-gray-600 fw-semibold fs-6'>Investment Readiness</div>
                        </div>
                    </div>
                </div>
            default:
                return <></>;
        }
    };
    const handleDownloadBusinessCaseReport = async () => {
        const newTab = window.open("/report-loading.html", "_blank");
        if (!newTab) {
            return;
        }
        try {
            setDownloadBusinessCaseReportLoader(true);
            const response = await downloadBusinessCaseReport(project.project_id);
            if (response.status !== "ok") {
                throw new Error(response.message);
            }
            newTab.location.replace(response.data.report_url);
        } catch (error) {
            newTab.close();
            showSnackbar(getApiErrorMessage(error, "Failed to download business case report."),
                "danger"
            );
        } finally {
            setDownloadBusinessCaseReportLoader(false);
        }
    };

    const rightSection = <div className="d-flex flex-column flex-end">
        <GateStepper project_id={project.project_id} clickable stages={stages} size="sm" shape="square" />
        <div className='d-flex flex-center align-items-center mt-5'>
            <div className='d-flex flex-column align-items-end'>
                <ThemeTooltip text={project.name} typeTooltip={'default'} Wrapper={<h3 className='fw-bold fs-4 text-primary mb-0 project-name w-450px text-end'>{project.name}</h3>} />
            </div>
        </div>
        <div id='kt_toolbar_right_section' className='mt-5 text-end d-flex'>
            {isViewOnly && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip>{VIEW_ONLY_MESSAGE}</Tooltip>}>
                    <span className='badge badge-light-warning fs-6 px-4 me-3 align-self-center d-inline-flex align-items-center gap-2'>
                        <Eye size={16} /> View only
                    </span>
                </OverlayTrigger>
            )}
            <PermissionGate allowed={can('reports', 'view')}>
                <button type='button' disabled={downloadBusinessCaseReportLoader} onClick={handleDownloadBusinessCaseReport} className='btn btn-lg btn-primary me-2 fs-3'>
                    <FileDown className="text-white me-2" size={24} />
                    Business Case
                    {downloadBusinessCaseReportLoader && <span className='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true'></span>}
                </button>
            </PermissionGate>
            {getStageRightSection()}
        </div>
    </div>;
    const description = <div className="row g-3 g-xl-5 mt-0">
        <div className="col-sm-3 col-6 mt-0">
            <ScoreCard
                title="Readiness"
                value="0"
                progress={0}
                status="primary"
                icon={<ShieldCheck className="text-blue-600" size={20} />}
            />
        </div>
        <div className="col-sm-3 col-6 mt-0">
            <ScoreCard
                title="Policy Alignment"
                value="0"
                progress={0}
                status="success"
                icon={<Target className="text-green-600" size={20} />}
            />
        </div>
        <div className="col-sm-3 col-6 mt-0">
            <ScoreCard
                title="Risk Score"
                value="0"
                progress={0}
                status="warning"
                icon={<TriangleAlert className="text-orange-500" size={20} />}
            />
        </div>
        <div className="col-sm-3 col-6 mt-0">
            <ScoreCard
                title="XP Points"
                value=""
                subtitle="0/5 quests"
                status="info"
                icon={<Trophy className="text-purple-600" size={20} />}
            />
        </div>
    </div>

    return (
        <div className='position-relative'>
            <PageTitle sitetitle={envConfig.APP_GOV_TITLE} breadcrumbs={breadCrumbs} description={description} purpose={
                stageData?.purpose} completionGuide={stageData?.completionGuide} subTitle={stageData?.subTitle} subText={stageData?.subText} subDescription={stageData?.subDescription}>{stageName}</PageTitle>
            <ManageProjectStageToolbar rightSection={rightSection} customPadding='pt-0 pb-3' />
            <Content>
                <ProjectStageProvider project={project} stage={stage} stages={stages}>
                    <LeftStepperProvider>
                        {/* Native title: hovering any (disabled) field explains why it is read-only. */}
                        <div title={isViewOnly ? VIEW_ONLY_MESSAGE : undefined}>
                            {handleLoadStage()}
                        </div>
                    </LeftStepperProvider>
                </ProjectStageProvider>
            </Content>
        </div>
    )
}

export { ManageProjectStage }